import { useEffect, useMemo, useState, useCallback } from 'react';
import { filterResponseHandlers } from 'src/components/BeaconV2Components/ExperimentalLayout/Summary/SummaryKeyMetricGrid';
import { FETCH_CHUNK_SIZE } from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import { useTableData } from 'src/serverProxy/useTableData';
import { useAppSelector } from 'src/utils/Hooks';

export const useUnitsOnHandData = (groupBy: string, entityIdList: string[], page: number) => {
  const { endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);
  const [dataByPageNumber, setDataByPageNumber] = useState({} as { [key: number]: any[] });

  const buildRequest = useCallback((requestBuilder) => {
    requestBuilder.addConditionTermFilter(groupBy, 'should', entityIdList);
    return requestBuilder;
  }, [groupBy, entityIdList]);

  const filterResponse = useMemo(() => {
    return filterResponseHandlers[groupBy] || (() => true);
  }, [groupBy]);

  const requestId = useMemo(() => {
    return `product-grid-units-on-hand-${groupBy}-${page}`;
  }, [groupBy, page]);

  const {
    isLoading: unitsOnHandRowsLoading,
    data: unitsOnHandRows,
    getValuesByFieldName,
    getValueDifferencesByFieldName
  } = useTableData<{ metadata: any }>({
    fields: [{ name: 'unitsOnHand' }],
    groupByFieldName: groupBy,
    indexName: 'sales',
    requestId,
    itemsPerPage: FETCH_CHUNK_SIZE,
    pageNumber: 1,
    startWeekId: endWeek,
    endWeekId: endWeek,
    customComparisonStartWeekId: comparisonEndWeek,
    customComparisonEndWeekId: comparisonEndWeek,
    shouldPerformFetch: !!entityIdList.length,
    buildRequest,
    filterResponse
  });

  const unitsOnHandData = useMemo(() => {
    const fieldValuesMap = [{ name: 'unitsOnHand' }].reduce((acc, { name }) => {
      return {
        ...acc,
        [name]: {
          values: getValuesByFieldName(name),
          differences: getValueDifferencesByFieldName(name)
        }
      };
    }, {} as { values: number[]; differences: number[] });

    return unitsOnHandRows.map((row, index) => ({
      ...row.metadata,
      ...[{ name: 'unitsOnHand' }].reduce((acc, { name }) => {
        return {
          ...acc,
          [name]: {
            value: fieldValuesMap[name].values[index],
            difference: fieldValuesMap[name].differences[index]
          }
        };
      }, {} as { [key: string]: number }),
      responseRow: row
    }));
  }, [getValueDifferencesByFieldName, getValuesByFieldName, unitsOnHandRows]);

  useEffect(() => {
    if (unitsOnHandData.length) {
      setDataByPageNumber((oldData) => ({
        ...oldData,
        [Math.ceil(page)]: unitsOnHandData
      }));
    }
  }, [unitsOnHandData, page]);

  const fullData = useMemo(() => {
    return Object.keys(dataByPageNumber)
      .reduce((acc, pageNumber) => {
        return [...acc, ...dataByPageNumber[Number(pageNumber)]];
      }, []);
  }, [dataByPageNumber]);

  return {
    data: fullData,
    isLoading: unitsOnHandRowsLoading
  };
};
